import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Schedule as TimeIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useDoctorShiftCsvUploader } from '../../hooks/useDoctorShiftCsvUploader';
import { useNotification } from '../../hooks/useNotification';
import NotificationSnackbar from '../common/NotificationSnackbar';
import { SAMPLE_CSV_DATA, CSV_FILE_CONFIG } from '../../constants/doctorShiftHeaders';
import { format } from 'date-fns';
import { getAuthTokenStatus } from '../../utils/apiClient';

/**
 * 医師シフトCSVアップローダーコンポーネント
 * 
 * 意図：
 * - 直感的なドラッグ&ドロップインターフェース
 * - リアルタイムバリデーション表示
 * - アップロード進捗の可視化
 * 
 * 注意点：
 * - タブ区切りCSVファイルのみ対応
 * - 最大5MBまでのファイルサイズ制限
 * 
 * 改善提案：
 * - ファイルのチャンク分割アップロード
 * - より詳細なエラー表示
 * - アップロード履歴の表示
 */
export const DoctorShiftCsvUploader: React.FC = () => {
  const theme = useTheme();
  const {
    file,
    isUploading,
    uploadProgress,
    error,
    validationErrors,
    previewData,
    uploadResult,
    handleFileSelect,
    uploadFile,
    reset
  } = useDoctorShiftCsvUploader();
  
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [isDragging, setIsDragging] = useState(false);
  const [showErrors, setShowErrors] = useState(true);
  const [showPreview, setShowPreview] = useState(true);

  // アップロード成功時にSnackbar通知を表示
  useEffect(() => {
    if (uploadResult && uploadResult.success) {
      const message = uploadResult.processedCount 
        ? `${uploadResult.processedCount}件のデータを正常に登録しました`
        : 'CSVファイルのアップロードが完了しました';
      showSuccess(message, 'アップロード成功');
    }
  }, [uploadResult, showSuccess]);

  // ドラッグ&ドロップハンドラー
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // ファイル選択ハンドラー
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // サンプルファイルダウンロード
  const downloadSample = useCallback(() => {
    const blob = new Blob([SAMPLE_CSV_DATA], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `医師シフトサンプル_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
  }, []);

  // ファイルサイズフォーマット
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 認証状態の取得（エラー時の詳細表示用）
  const authStatus = getAuthTokenStatus();

  return (
    <Box>
      {/* アップロードエリア */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 1,
          border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
          bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept={CSV_FILE_CONFIG.acceptedTypes.join(',')}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          id="doctor-shift-file-input"
        />
        
        <label htmlFor="doctor-shift-file-input" style={{ cursor: 'pointer' }}>
          <Box sx={{ textAlign: 'center' }}>
            <motion.div
              animate={{ y: isDragging ? -10 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <UploadIcon sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
            </motion.div>
            
            <Typography variant="h6" gutterBottom>
              CSVファイルをドラッグ&ドロップ
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              または
            </Typography>
            <Button variant="outlined" component="span" sx={{ mb: 2 }}>
              ファイルを選択
            </Button>
            
            <Typography variant="caption" display="block" color="text.secondary">
              タブ区切りCSVファイル（最大{CSV_FILE_CONFIG.maxSizeInMB}MB）
            </Typography>
            
            <Link
              component="button"
              variant="caption"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                downloadSample();
              }}
              sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <DownloadIcon fontSize="small" />
              サンプルファイルをダウンロード
            </Link>
          </Box>
        </label>

        {/* プログレスバー */}
        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: 4 }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Paper>

      {/* ファイル情報 */}
      <AnimatePresence>
        {file && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)} • {previewData.length}件のデータ
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={reset}
                  disabled={isUploading}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* エラー表示 */}
      <AnimatePresence>
        {(error || validationErrors.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              severity="error"
              sx={{ mt: 3, borderRadius: 2 }}
              action={
                validationErrors.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => setShowErrors(!showErrors)}
                  >
                    {showErrors ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>
                )
              }
            >
              <Typography variant="subtitle2" gutterBottom>
                {error || 'データにエラーがあります'}
              </Typography>
              
              <Collapse in={showErrors}>
                {/* バリデーションエラー表示 */}
                {validationErrors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {validationErrors.slice(0, 5).map((err, index) => (
                      <Typography key={index} variant="caption" display="block">
                        • {err.row}行目 - {err.field}: {err.message}
                      </Typography>
                    ))}
                    {validationErrors.length > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        他{validationErrors.length - 5}件のエラー
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* 認証エラーの詳細情報 */}
                {error && (error.includes('認証') || error.includes('Auth driver') || error.includes('sanctum')) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1 }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 1 }}>
                      認証デバッグ情報:
                    </Typography>
                    <Typography variant="caption" display="block">
                      • 認証トークン: {authStatus.hasToken ? '設定済み' : '未設定'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      • リクエストヘッダー: {authStatus.hasAuthHeader ? '設定済み' : '未設定'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      • バックエンドURL: {process.env.REACT_APP_API_BASE_URL || '未設定'}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                      ※ GET リクエストは正常に動作しますが、POST リクエストで認証エラーが発生しています
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* プレビューテーブル */}
      <AnimatePresence>
        {previewData.length > 0 && validationErrors.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Paper elevation={0} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[50],
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle2">データプレビュー</Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={showPreview}>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>医師id</TableCell>
                        <TableCell>医師名</TableCell>
                        <TableCell>シフト日付</TableCell>
                        <TableCell>診療開始時間</TableCell>
                        <TableCell>診療終了時間</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.slice(0, 10).map((row: any, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Chip
                              icon={<DoctorIcon />}
                              label={row.doctor_id}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.doctor_full_name || row.doctor_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<DateIcon />}
                              label={row.shift_date}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<TimeIcon />}
                              label={row.start_time}
                              size="small"
                              sx={{
                                backgroundColor: '#fce4ec',
                                color: '#e91e63',
                                '& .MuiChip-icon': {
                                  color: '#e91e63'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<TimeIcon />}
                              label={row.end_time}
                              size="small"
                              sx={{
                                backgroundColor: '#fff3e0',
                                color: '#ff6f00',
                                '& .MuiChip-icon': {
                                  color: '#ff6f00'
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {previewData.length > 10 && (
                  <Box sx={{ p: 1, textAlign: 'center', bgcolor: theme.palette.grey[50] }}>
                    <Typography variant="caption" color="text.secondary">
                      他{previewData.length - 10}件のデータ
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* アップロード結果（成功・エラー両方表示） */}
      <AnimatePresence>
        {uploadResult && uploadResult.success && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              severity="success"
              icon={<SuccessIcon />}
              sx={{ 
                mt: 3, 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.success.main, 0.1),
              }}
            >
              <Typography variant="subtitle2">
                {uploadResult.message || 'シフトデータを正常に登録しました'}
              </Typography>
              {uploadResult.processedCount !== undefined && (
                <Typography variant="caption" display="block">
                  登録件数: {uploadResult.processedCount}件
                </Typography>
              )}
            </Alert>
          </motion.div>
        )}
        {uploadResult && !uploadResult.success && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              sx={{ 
                mt: 3, 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              }}
            >
              <Typography variant="subtitle2">
                {uploadResult.message}
              </Typography>
              {uploadResult.errorCount !== undefined && (
                <Typography variant="caption" display="block">
                  エラー: {uploadResult.errorCount}件
                </Typography>
              )}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* アクションボタン */}
      <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={reset}
          disabled={isUploading}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={uploadFile}
          disabled={!file || validationErrors.length > 0 || isUploading}
          startIcon={isUploading ? <></> : <UploadIcon />}
          sx={{
            minWidth: 120,
            position: 'relative',
          }}
        >
          {isUploading ? `アップロード中... ${uploadProgress}%` : 'アップロード'}
        </Button>
      </Stack>

      {/* 通知スナックバー */}
      <NotificationSnackbar
        notification={notification}
        onClose={hideNotification}
      />
    </Box>
  );
};
